import React from 'react'
import cn from 'classnames'
import WithTooltip from 'components/shared/WithTooltip'
import { Monitor, Moon, Sun } from 'lucide-react'

export type AppearanceTheme = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

interface ThemeToggleButtonProps {
    theme: AppearanceTheme
    effectiveTheme: EffectiveTheme
    onToggle: () => void
    className?: string
    iconSize?: number
}

function getThemeTooltip(theme: AppearanceTheme) {
    if (theme === 'light') return 'Using light mode<br/>Click to switch to dark mode'
    if (theme === 'dark') return 'Using dark mode<br/>Click to switch to system theme'
    return 'Using system theme<br/>Click to switch to light mode'
}

function renderThemeIcon(theme: AppearanceTheme, effectiveTheme: EffectiveTheme, iconSize: number) {
    if (theme === 'system') {
        return (
            <div
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {effectiveTheme === 'dark' ? <Moon size={iconSize} /> : <Sun size={iconSize} />}
                <Monitor
                    size={Math.max(8, Math.round(iconSize / 2))}
                    style={{
                        position: 'absolute',
                        bottom: -3,
                        right: -4,
                        strokeWidth: 2.5,
                    }}
                />
            </div>
        )
    }

    return theme === 'light' ? <Sun size={iconSize} /> : <Moon size={iconSize} />
}

export default function ThemeToggleButton({
    theme,
    effectiveTheme,
    onToggle,
    className,
    iconSize = 18,
}: ThemeToggleButtonProps) {
    const tooltip = getThemeTooltip(theme)

    return (
        <WithTooltip tip={tooltip} html>
            <button
                type="button"
                data-testid="theme-toggle"
                className={cn(className)}
                onClick={onToggle}
                aria-label={tooltip.replace('<br/>', ' - ')}
            >
                {renderThemeIcon(theme, effectiveTheme, iconSize)}
            </button>
        </WithTooltip>
    )
}
